import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ClientLink from './ClientLink';
import { Bookmark } from 'lucide-react';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';

// The loan applications this back-office user saved to finish later
// (GET /api/loan-drafts), on their dashboard. Continue opens the client's
// Add Loan with it (/manage-clients?resumeDraft=<clientId>); discard deletes
// it. They are also reminded by email daily until each is added or
// discarded. Renders nothing when there are none.
const StaffSavedApplications = ({ className = 'mb-4' }) => {
  const [drafts, setDrafts] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-drafts'), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setDrafts(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Not available — nothing to show.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!drafts.length) return null;

  const discard = async (clientId) => {
    setBusyId(clientId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(apiUrl(`/api/loan-drafts/client/${clientId}`), { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* already gone */ }
    setDrafts((list) => list.filter((d) => d.client_id !== clientId));
    setBusyId(null);
  };

  return (
    <div className={`${className} rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Bookmark size={16} className="text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
          Your saved loan applications ({drafts.length})
        </h3>
        <span className="text-xs text-blue-800 dark:text-blue-300">— not added yet; you are reminded daily until each is added or discarded</span>
      </div>
      <ul className="divide-y divide-blue-100 dark:divide-slate-700">
        {drafts.map((d) => {
          const months = Number(d.term_months || d.duration_months) || null;
          return (
            <li key={d.client_id} className="py-2 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                  <ClientLink clientId={d.client_id} name={d.client_name || `Client #${d.client_id}`} />
                  {d.client_code && <span className="ml-1 text-xs text-gray-500 dark:text-slate-400">({d.client_code})</span>}
                </p>
                <p className="text-xs text-gray-600 dark:text-slate-400">
                  {d.principal_amount ? toCurrency(d.principal_amount) : 'Amount not entered'}
                  {months ? ` over ${months} month${months === 1 ? '' : 's'}` : ''}
                  {d.saved_at ? ` · saved ${formatDateDMY(d.saved_at)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => discard(d.client_id)}
                  disabled={busyId === d.client_id}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/manage-clients?resumeDraft=${d.client_id}`)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                >
                  Continue
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StaffSavedApplications;
